/**
 * RÉVISION À LA MAISON — page publique du client privé : techniques travaillées + vidéos démo.
 * Public : GET /api/public/private-clients/review/{slug}
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const LEVELS = ["", "Découverte", "En apprentissage", "Acquis avec aide", "Autonome", "Maîtrisé"];

export function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  if (/b-cdn\.net|mediadelivery\.net|\/embed\//.test(url)) return url;
  return null;
}

function Video({ url, testId }) {
  const embed = toEmbed(url);
  if (embed) return <iframe src={embed} title="Vidéo démo" allow="autoplay; fullscreen" allowFullScreen className="w-full aspect-video rounded-xl border border-zinc-800 bg-black" data-testid={testId} />;
  return <a href={url} target="_blank" rel="noreferrer" className="inline-block text-amber-300 underline text-sm" data-testid={testId}>🎬 Ouvrir la vidéo démo</a>;
}

export default function TechniquesRevisionPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/public/private-clients/review/${slug}`).then((r) => setData(r.data)).catch(() => setErr("Lien de révision introuvable ou expiré."));
  }, [slug]);

  if (err) return <div className="min-h-screen bg-zinc-950 text-zinc-300 flex items-center justify-center p-6" data-testid="review-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center">Chargement…</div>;
  const withVideo = data.techniques.filter((t) => t.video_url);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-6 py-10 max-w-3xl mx-auto" data-testid="review-page">
      <div className="text-amber-400 text-xs tracking-[0.35em] uppercase font-bold">Institut Impact · Coaching privé</div>
      <h1 className="text-4xl sm:text-5xl font-black mt-1">Ta révision, {data.client.first_name} 🥋</h1>
      <p className="text-zinc-400 mt-2">Les techniques travaillées avec ton coach, avec les vidéos démo pour réviser à la maison. Un degré chaque jour.</p>

      <section className="mt-8">
        <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-zinc-300 mb-3">🎬 Vidéos démo ({withVideo.length})</h2>
        {withVideo.length === 0 ? (
          <div className="text-zinc-500 text-sm border border-dashed border-zinc-800 rounded-xl p-6 text-center" data-testid="review-no-videos">Aucune vidéo pour l'instant — ton coach en ajoutera après tes prochaines séances.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="review-videos">
            {withVideo.map((t, i) => (
              <div key={t.name} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-3 space-y-2">
                <Video url={t.video_url} testId={`review-video-${i}`} />
                <div className="font-bold">{t.name}</div>
                <div className="text-xs text-zinc-400">{t.category}{t.last_level ? ` · ${LEVELS[t.last_level] || "niv. " + t.last_level}` : ""} · travaillée ×{t.count}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-10">
        <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-zinc-300 mb-3">📓 Séance par séance</h2>
        <div className="space-y-3" data-testid="review-sessions">
          {data.sessions.map((s) => (
            <div key={s.id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500">{(s.date_heure || "").slice(0, 10)}{s.focus ? ` · ${s.focus}` : ""}</div>
              <ul className="mt-2 flex flex-wrap gap-2">
                {s.techniques.map((t, i) => (
                  <li key={i} className="text-sm px-2.5 py-1 rounded-full border border-zinc-700 bg-zinc-950/60">
                    {t.name}{t.level ? <span className="text-amber-300"> · {LEVELS[t.level] || t.level}</span> : ""}{t.video_url && <a href={t.video_url} target="_blank" rel="noreferrer" className="ml-1">🎬</a>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {data.sessions.length === 0 && <div className="text-zinc-500 text-sm">Aucune séance avec techniques pour l'instant.</div>}
        </div>
      </section>
    </div>
  );
}
